/**
 * 可视项目管理器，用于跟踪gallery中哪些项目当前可见
 * 参考VirtualRCTableView中的VisibleRowsManager实现
 */
export class VisibleItemsManager {
  private indices: Set<number>;
  private _minIndex: number;
  private _maxIndex: number;

  constructor() {
    this.indices = new Set<number>();
    this._minIndex = -1;
    this._maxIndex = -1;
  }

  get minIndex(): number {
    return this._minIndex;
  }

  get maxIndex(): number {
    return this._maxIndex;
  }

  updateIndices(index: number, isVisible: boolean): void {
    if (isVisible) {
      this.indices.add(index);
    } else {
      this.indices.delete(index);
    }

    this.recalculateRange();
  }

  private recalculateRange(): void {
    if (this.indices.size === 0) {
      this._minIndex = -1;
      this._maxIndex = -1;
      return;
    }

    const validIndices = Array.from(this.indices).filter((idx) => idx >= 0);
    if (validIndices.length > 0) {
      this._minIndex = Math.min(...validIndices);
      this._maxIndex = Math.max(...validIndices);
    } else {
      this._minIndex = -1;
      this._maxIndex = -1;
    }
  }

  getVisibleIndices(): number[] {
    return Array.from(this.indices)
      .filter((idx) => idx >= 0)
      .sort((a, b) => a - b);
  }

  getVisibleRange(): { min: number; max: number } {
    return { min: this._minIndex, max: this._maxIndex };
  }

  clear(): void {
    this.indices.clear();
    this._minIndex = -1;
    this._maxIndex = -1;
  }

  shouldBeVisible(index: number, preloadItemCount: number): boolean {
    // 如果没有可见项目，显示所有项目（智能初始状态）
    if (this._minIndex === -1 || this._maxIndex === -1) {
      return true;
    }

    // 如果索引无效，也显示
    if (index < 0) {
      return true;
    }

    // 对于快速滚动优化：增加动态缓冲区
    // 根据可见范围大小动态调整预加载区域
    const visibleRange = this._maxIndex - this._minIndex;
    const dynamicBuffer = Math.max(
      preloadItemCount,
      Math.floor(visibleRange * 1.5),
    );

    const preloadStart = Math.max(0, this._minIndex - dynamicBuffer);
    const preloadEnd = this._maxIndex + dynamicBuffer;

    return index >= preloadStart && index <= preloadEnd;
  }
}
